import { useState, useEffect, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Music, ExternalLink, BookOpen, Loader, Share2, Headphones, Sparkles, Youtube, FileText, QrCode } from 'lucide-react';
import { Logo } from '../components/Logo';
import { getApiBase } from '../utils/api';
import { getQRBranding, QRBranding } from '../utils/branding';
import { QRPageFooter } from '../components/qr/QRPageFooter';
import { PDFProductionWrapper } from '../components/qr/PDFProductionWrapper';
import { YouTubeProductionWrapper } from '../components/qr/YouTubeProductionWrapper';
import { WebsiteProductionWrapper } from '../components/qr/WebsiteProductionWrapper';
import { SpotifyProductionWrapper } from '../components/qr/SpotifyProductionWrapper';
import React from 'react';

// Helper component for branding header
const BrandingHeader = ({ branding, onShare }: { branding: QRBranding | null; onShare: () => void }) => {
  if (!branding?.showBranding) return null;

  const renderBrandingContent = () => {
    // Logo only
    if (branding.displayType === 'logo_only' && branding.logoUrl) {
      return (
        <img 
          src={branding.logoUrl} 
          alt={branding.companyName || 'Logo'} 
          className="h-10 object-contain"
        />
      );
    }
    
    // Name only
    if (branding.displayType === 'name_only' && branding.companyName) {
      return (
        <span 
          className="font-bold text-xl"
          style={{ color: branding.textColor || '#FFFFFF' }}
        >
          {branding.companyName}
        </span>
      );
    }
    
    // Logo and name (default)
    if (branding.displayType === 'logo_and_name' || !branding.displayType) {
      // Special case for BeyondPages
      if (branding.companyName === 'BeyondPages' && !branding.logoUrl) {
        return (
          <Link to="/" className="flex items-center gap-3 text-white hover:opacity-80 transition-opacity">
            <Logo size={40} />
            <span className="font-bold text-xl">BeyondPages</span>
          </Link>
        );
      }
      
      return (
        <div className="flex items-center gap-3">
          {branding.logoUrl && (
            <img 
              src={branding.logoUrl} 
              alt={branding.companyName || 'Logo'} 
              className="h-10 object-contain"
            />
          )}
          {branding.companyName && (
            <span 
              className="font-bold text-xl"
              style={{ color: branding.textColor || '#FFFFFF' }}
            >
              {branding.companyName}
            </span>
          )}
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="relative z-10">
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          {renderBrandingContent()}
          <button
            onClick={onShare}
            className="text-white/80 hover:text-white transition-colors p-2"
            title="Teilen"
          >
            <Share2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

interface LinkData {
  id: string;
  title: string;
  slug: string;
  target_url: string;
  type?: 'website' | 'spotify' | 'youtube' | 'pdf' | 'multishop' | 'author' | 'sample';
  pdf_url?: string;
  book_title?: string;
  author?: string;
  cover_url?: string;
  branding_type?: 'beyondpages' | 'custom' | 'none';
  author_id?: string;
}

export default function Listen() {
  const { slug, accountSlug } = useParams<{ slug: string; accountSlug?: string }>();
  const [linkData, setLinkData] = useState<LinkData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [branding, setBranding] = useState<QRBranding | null>(null);

  useEffect(() => {
    const fetchLinkData = async () => {
      try {
        setLoading(true);
        
        // DEBUGGING: Log all URL and navigation details
        console.log('\n🔍 LISTEN PAGE DEBUG - Page loaded');
        console.log('📌 Slug:', slug);
        console.log('📌 Account Slug:', accountSlug);
        console.log('📌 Current URL:', window.location.href);
        console.log('📌 URL pathname:', window.location.pathname);
        console.log('📌 URL search:', window.location.search);
        console.log('📌 URL hash:', window.location.hash);
        console.log('📌 Document referrer:', document.referrer);
        try {
          const hasPerf = typeof performance !== 'undefined' && typeof (performance as any).getEntriesByType === 'function';
          const navEntries = hasPerf ? (performance as any).getEntriesByType('navigation') : [];
          const navType = navEntries && navEntries.length > 0 ? (navEntries[0] as any).type : 'unknown';
          console.log('📌 Navigation type:', navType);
        } catch (e) {
          console.log('📌 Navigation type: unknown');
        }
        console.log('📌 User Agent:', navigator.userAgent);
        
        // In production, use relative URLs that go through Netlify redirects
        // In development, use the full API URL
        const API_BASE = getApiBase()
        console.log('🌐 API Base URL:', API_BASE);
        
        // Use the appropriate endpoint based on whether we have an account slug
        const endpoint = accountSlug 
          ? `${API_BASE}/api/public/link/${accountSlug}/${slug}`
          : `${API_BASE}/api/public/link/${slug}`;
        
        console.log('🔗 Fetching link data from:', endpoint);
          
        const response = await fetch(endpoint);
        console.log('📡 Response status:', response.status);
        console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));
        
        if (!response.ok) {
          throw new Error('Link not found');
        }
        
        const data = await response.json();
        console.log('✅ Link data received:', data);
        setLinkData(data);
        
        // Load branding
        const brandingConfig = await getQRBranding(data.branding_type, data.author_id);
        setBranding(brandingConfig);
        console.log('🎨 Branding config:', brandingConfig);
        
        // Check if this link will be redirected to another page
        const willRedirect = data.type === 'multishop' || data.type === 'author' || data.type === 'sample';
        console.log('🔄 Will redirect to specialized page?', willRedirect, 'Type:', data.type);
        
        // Check if this is a preview (from the QR management page)
        const urlParams = new URLSearchParams(window.location.search);
        const isPreview = urlParams.get('preview') === 'true';
        
        // NOTE: Scan recording has been moved to backend redirect.ts for single source of truth
        // This page is now only used for previews and specialized content display
        console.log('ℹ️ Listen page loaded - preview mode:', isPreview);
        
        console.log('\n✅ LISTEN PAGE - Setup complete\n');
      } catch (err) {
        console.error('🎵 Listen: Error fetching link data:', err);
        console.error('🎵 Error details:', {
          message: err.message,
          stack: err.stack
        });
        setError('Link not found or expired');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      console.log('🚀 Starting fetchLinkData for slug:', slug);
      fetchLinkData();
    } else {
      console.log('⚠️ No slug provided, skipping fetch');
    }
  }, [slug, accountSlug]);

  // Auto-redirect for websites that cannot be embedded (friendly delay)
  useEffect(() => {
    if (!linkData) return
    // Infer effective type similar to render logic
    let inferredType = linkData.type as any
    if (!inferredType) {
      if (linkData.target_url?.includes('spotify.com')) inferredType = 'spotify'
      else if (linkData.target_url?.includes('youtube.com') || linkData.target_url?.includes('youtu.be')) inferredType = 'youtube'
      else inferredType = 'website'
    }
    if (inferredType === 'website' && (linkData as any)?.metadata?.iframe_compatible === false) {
      const t = setTimeout(() => {
        window.location.href = linkData.target_url
      }, 1200)
      return () => clearTimeout(t)
    }
  }, [linkData])

  const extractSpotifyId = (url: string): string | null => {
    const match = url.match(/spotify\.com\/playlist\/([a-zA-Z0-9]+)/);
    return match ? match[1] : null;
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: linkData?.title || 'BeyondPages Playlist',
          text: `Hör dir diese Playlist an: ${linkData?.title}`,
          url: window.location.href
        });
      } catch (err) {
        // User cancelled share
      }
    } else {
      // Fallback: Copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link wurde in die Zwischenablage kopiert!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-white mx-auto mb-4" />
          <p className="text-white/80">Lade Playlist...</p>
        </div>
      </div>
    );
  }

  if (error || !linkData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-slate-900 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <QrCode className="w-20 h-20 text-white/40 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Inhalt nicht verfügbar</h1>
          <p className="text-white/60 mb-6">
            Der angeforderte Inhalt konnte nicht geladen werden.
            Bitte überprüfen Sie den QR-Code oder kontaktieren Sie den Support.
          </p>
          <Link to="/" className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            Zur Startseite
          </Link>
        </div>
      </div>
    );
  }

  const spotifyId = extractSpotifyId(linkData.target_url);
  const bookCover = linkData.cover_url || 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&h=600&fit=crop';

  // TEMPORARY: Auto-detect type based on URL if type is missing
  let effectiveType = linkData.type;
  if (!effectiveType) {
    if (linkData.target_url.includes('spotify.com')) {
      effectiveType = 'spotify';
    } else if (linkData.target_url.includes('youtube.com') || linkData.target_url.includes('youtu.be')) {
      effectiveType = 'youtube';
    } else {
      effectiveType = 'website';
    }
  }

  // Extract YouTube video ID
  const extractYouTubeId = (url: string): string | null => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
    return match ? match[1] : null;
  };

  // Route to specialized pages for new types
  // Preserve the preview parameter when redirecting
  const urlParams = new URLSearchParams(window.location.search);
  const isPreview = urlParams.get('preview') === 'true';
  const previewParam = isPreview ? '?preview=true' : '';
  
  if (effectiveType === 'multishop') {
    return <Navigate to={`/multishop/${slug}${previewParam}`} replace />;
  }
  
  if (effectiveType === 'author') {
    return <Navigate to={`/author/${slug}${previewParam}`} replace />;
  }
  
  if (effectiveType === 'sample') {
    return <Navigate to={`/sample/${slug}${previewParam}`} replace />;
  }

  // For website type, render exactly like the preview (no auto-embed);
  if (effectiveType === 'website') {
    return (
      <div className="min-h-screen">
        <WebsiteProductionWrapper 
          linkId={slug}
          linkData={linkData}
          accountSlug={accountSlug}
          isPreview={true}
        />
      </div>
    );
  }

  // For YouTube type
  if (effectiveType === 'youtube') {
    return (
      <div className="min-h-screen">
        <YouTubeProductionWrapper 
          linkId={slug}
          linkData={linkData}
          accountSlug={accountSlug}
          isPreview={true}
        />
      </div>
    );
  }

  // For PDF type
  if (effectiveType === 'pdf') {
    return (
      <div className="min-h-screen">
        <PDFProductionWrapper 
          linkId={slug}
          linkData={linkData}
          accountSlug={accountSlug}
          isPreview={true}
        />
      </div>
    );
  }

  // For Spotify type -> use unified production wrapper
  if (effectiveType === 'spotify' || !effectiveType) {
    return (
      <div className="min-h-screen">
        <SpotifyProductionWrapper 
          linkId={slug}
          linkData={linkData}
          accountSlug={accountSlug}
          isPreview={true}
        />
      </div>
    );
  }

  // Default fallback - better UX than blank page
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <QrCode className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          QR-Code wird geladen...
        </h1>
        <p className="text-gray-600 mb-4">
          Falls das Problem bestehen bleibt, kontaktieren Sie bitte den Support.
        </p>
        {process.env.NODE_ENV === 'development' && (
          <pre className="mt-4 p-4 bg-gray-100 rounded text-xs text-left overflow-auto">
            Debug Info:{'\n'}
            Type: {effectiveType || 'undefined'}{'\n'}
            Slug: {slug}
          </pre>
        )}
        <Link
          to="/"
          className="inline-block mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          Zur Startseite
        </Link>
      </div>
    </div>
  );
}
